import { formatDate } from "@/lib/format";
// web/src/pages/clients/components/ClientQuotesTab.tsx
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { quotesApi, type QuoteListItemDto } from "@/lib/quotes";
import { QuoteStatusBadge } from "@/components/quotes/quote-status-badge";
import { EntityEmptyStub } from "@/components/detail/EntityEmptyStub";

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

const PAGE_SIZE = 10;

export interface ClientQuotesTabProps {
  clientId: number;
}

/**
 * Inline mini-list of the most recent quotes for a client, displayed on
 * ClientDetailPage's Quotes tab. The "View all" link deep-links into the
 * QuotesListPage with `?clientId=N` applied.
 *
 * Backend ordering: server default (no `sort` param on quotesApi.list).
 * Demo-acceptable for "top 10"; a follow-up plan can add explicit sorting.
 */
export function ClientQuotesTab({ clientId }: ClientQuotesTabProps) {
  const query = useQuery({
    queryKey: ["client-quotes", clientId, PAGE_SIZE],
    queryFn: () => quotesApi.list({ clientId, page: 0, pageSize: PAGE_SIZE }),
    enabled: clientId > 0,
  });

  if (query.isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-10 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  const rows = query.data?.items ?? [];

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        <EntityEmptyStub
          icon={FileText}
          title="No quotes for this client yet"
          helper="Quotes raised for this client will appear here."
        />
        <div className="text-right">
          <Link to={`/quotes?clientId=${clientId}`} className="text-sm text-primary underline">
            View all quotes for this client →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Quote Ref</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Total inc GST</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: QuoteListItemDto) => (
              <tr key={r.quoteId} className="border-t border-border">
                <td className="px-3 py-2">
                  <Link to={`/quotes/${r.quoteId}`} className="text-primary underline">
                    {r.quoteRef}
                  </Link>
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {r.createdOn ? formatDate(r.createdOn) : "—"}
                </td>
                <td className="px-3 py-2">
                  <QuoteStatusBadge status={r.status} />
                </td>
                <td className="px-3 py-2 text-right">
                  {r.totalIncGst != null ? fmt.format(r.totalIncGst) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-right">
        <Link to={`/quotes?clientId=${clientId}`} className="text-sm text-primary underline">
          View all quotes for this client →
        </Link>
      </div>
    </div>
  );
}
